/**
 * Desktop MY SCORES (DESIGN-CHANGELOG 2026-09-12 "Scoring is not a backlog
 * privilege · MY SCORES"; `design/GT Mine.dc.html`, the `isScores` block).
 *
 * Full-screen behind MY GAMES' second door, the same bar as BACKLOG. The
 * page's own first row is the stat line — `N GAMES SCORED · AVG 8.3 · HIGH 10`
 * — and a right-aligned note, `A SCORE IS WHAT YOU PLAYED · THE PARTY SEES
 * IT`. Then its own search box (`rtQ`, `/` focuses it), the match note and the
 * shared `+ FILTERS`, exactly as BACKLOG carries them, and a 168px auto-fill
 * grid of 2:3 tiles sorted high → low.
 *
 * THE TILE IS A BACKLOG TILE WITHOUT THE ✕: same cover, same bottom-gradient
 * caption, the shell's glossy tilt + sheen hover. The score badge top-right IS
 * the score control (the one YOUR SCORE modal); under the name, `WAS 6 · 2D
 * AGO` — a re-score keeps its prior value, so the ledger says what changed.
 * `◈` marks a game that is also on your backlog. The grid ends with a dashed
 * `SCORE ANY GAME` tile that opens DISCOVER: the point of the page is that
 * scoring never needed the shelf.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { scoresVM } from '../../store/selectors.ts';
import type { ScoreRow } from '../../store/selectors.ts';
import FilterBar from '../components/FilterBar.tsx';
import PageBar from '../components/PageBar.tsx';
import { useSearchInputRef } from '../searchHotkey.ts';
import { useHoverTilt } from '../useHoverTilt.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function ScoreTile({ s: row }: { s: ScoreRow }): JSX.Element {
  const st = useQd();
  const art = useArt(row.id + 'p', row.coverCands);
  const tilt = useHoverTilt('rt:' + row.id);

  return (
    <div
      ref={tilt.ref}
      {...tilt.handlers}
      onClick={() => st.openGame(row.id)}
      style={{
        position: 'relative',
        aspectRatio: '2/3',
        background: row.fall,
        overflow: 'hidden',
        border: '1px solid var(--gt-border)',
        cursor: 'pointer',
        ...tilt.rest,
      }}
    >
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={row.ini} hasCover={art.hasCover} cover={art.cover} />
      <div
        style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 38%,rgba(5,5,8,.97) 78%)' }}
      />
      <div ref={tilt.sheenRef} style={tilt.restSheen} />

      {/* the score badge IS the score control — its edge is the score's colour */}
      <div
        onClick={(ev) => {
          ev.stopPropagation();
          st.openScore(row.id);
        }}
        data-tip={row.scTip}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          background: 'var(--gt-ink)',
          borderLeft: '1px solid ' + row.scClr,
          borderBottom: '1px solid ' + row.scClr,
          padding: '6px 9px',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontFamily: PIXEL, fontSize: 15, color: row.scClr, display: 'flex', alignItems: 'center' }}>
          <span style={{ transform: 'translateY(0.13em)' }}>{row.scTxt}</span>
        </span>
        <span style={{ fontFamily: MONO, fontSize: 6, letterSpacing: '.12em', color: 'var(--gt-muted-2)' }}>MY SCORE</span>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          padding: '11px 11px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 12.5,
            lineHeight: 1.2,
            color: '#fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.name}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '.03em',
              color: 'var(--gt-text-5)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.when}
          </span>
          {row.srcShow ? (
            <span data-tip="ALSO ON YOUR BACKLOG" style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-text-4)', flex: 'none' }}>
              ◈
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function Scores(): JSX.Element {
  const s = useQd();
  const v = scoresVM(s);
  const rtRef = useSearchInputRef();

  // Same art fetch the two shelves do, for the same reason (`screens/Backlog.tsx`):
  // a game scored from GAME DETAIL mid-session may be on no surface that has
  // asked for its assets, and a modern appid has no art without them.
  useQueueArt(v.rtRows.map((r) => r.id));

  return (
    <div data-screen-label="My scores" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageBar title="MY SCORES" />

      {/* the page's own first row: the stat, and what a score is */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px 0', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--acc)', whiteSpace: 'nowrap' }}>
          {v.rtSub}
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--gt-muted-2)', whiteSpace: 'nowrap' }}>
          {v.rtNoteLine}
        </span>
      </div>

      {/* search · match note · + FILTERS ---------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px 0', flexWrap: 'wrap' }}>
        <SearchBox
          value={v.rtQ}
          onChange={(e) => s.setRtQ(e.target.value)}
          onClear={() => s.setRtQ('')}
          inputRef={rtRef}
          placeholder="SEARCH WHAT YOU SCORED…"
          w="230px"
          pfs={11}
          ifs={10.5}
          pad="7px 10px"
        />
        {v.hasRtNote ? (
          <span style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)', whiteSpace: 'nowrap' }}>{v.rtNote}</span>
        ) : null}
        <FilterBar />
      </div>

      {v.rtNarrowed ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: MONO, fontSize: 11, lineHeight: 2, color: 'var(--gt-muted)' }}>
          {v.rtNarrowTxt}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(168px,1fr))',
          gap: 14,
          padding: '16px 24px 30px',
        }}
      >
        {v.rtRows.map((row) => (
          <ScoreTile key={row.key} s={row} />
        ))}
        {/* the dashed tile ends the grid unconditionally: any game can be scored */}
        <div
          onClick={() => s.nav('discover')}
          data-tip="OPEN ANY GAME AND SCORE IT — IT DOES NOT HAVE TO BE ON YOUR BACKLOG"
          className="d-hov-acc-bd"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            border: '1px dashed var(--gt-border-3)',
            minHeight: 200,
            cursor: 'pointer',
          }}
        >
          <span style={{ fontFamily: PIXEL, fontSize: 16, color: 'var(--gt-ghost-2)' }}>+</span>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-muted-2)', textAlign: 'center', lineHeight: 1.8 }}>
            SCORE ANY
            <br />
            GAME
          </span>
        </div>
      </div>

      {v.rtEmpty ? (
        <div style={{ padding: '0 24px 40px', fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', lineHeight: 1.8, color: 'var(--gt-muted)' }}>
          {v.rtEmptyTxt}
        </div>
      ) : null}
    </div>
  );
}
